// src/hooks/useFieldSchema.ts
// Hook to fetch field metadata (types, choices) from backend

import { useEffect, useState, useCallback } from "react";
import { fetchData } from "api/apiHelpers";
import logger from "utils/logger";

export interface FieldChoice {
  value: string | number | null;
  display_name: string;
}

export interface FieldInfo {
  type: "choice" | "date" | "datetime" | "integer" | "decimal" | "boolean" | "text" | "string" | "reference";
  choices?: FieldChoice[];
  /** For `type === "reference"` (a FK rendered as a manual picker): the table
   *  type whose rows are the selectable options (e.g. "diagnosistable"). */
  reference_table_type?: string;
  /** For `type === "reference"`: the field on the referenced row to use as the
   *  option label (e.g. "cancer_type"). */
  reference_label_field?: string;
  help_text?: string;
  verbose_name?: string;
  required?: boolean;
  max_length?: number;
  max_digits?: number;
  decimal_places?: number;
  /** Backend-declared read-only field (e.g. SNOMED `_code` columns). Stays
   *  VISIBLE but is rendered as plain text, never an editable input, so a user
   *  cannot hand-edit a code out of sync with its resolved label. */
  read_only?: boolean;
}

export interface FieldSchema {
  domain_key: string;
  table_type: string;
  model_name: string;
  fields: Record<string, FieldInfo>;
  /** Optional per-table primary-columns override (display-only). Names the
   *  columns the compact table should surface up-front; absent/empty → the
   *  default first-4 fallback. Backward-compatible additive key. */
  primary_columns?: string[];
  /** Optional i18n key for the per-cancer panel title (e.g. the universal
   *  CancerProfileTable resolves to "clinical.cancer_profile_skin" for melanoma).
   *  Null / absent → the caller falls back to its generic default title. */
  panel_label_key?: string | null;
  /** Columns HIDDEN for this cancer because they are another cancer's residue
   *  (backend `foreign_scoped_fields` — the same set dropped from `fields`). The
   *  sidebar/field-group nav uses this to omit foreign-cancer residue GROUPS
   *  (e.g. "Prostate Residue" for a breast patient) while keeping in-scope base
   *  groups whose fields are locked/derived (e.g. "Cancer" → cancer_type). Empty
   *  / absent for non-scoped tables. */
  hidden_scoped_fields?: string[];
}

// Cache for field schemas
const schemaCache = new Map<string, FieldSchema>();

export function useFieldSchema(domainKey: string, tableType: string) {
  const [schema, setSchema] = useState<FieldSchema | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cacheKey = `${domainKey}:${tableType}`;

  useEffect(() => {
    if (!domainKey || !tableType) return;

    const fetchSchema = async () => {
      // Check cache first
      if (schemaCache.has(cacheKey)) {
        setSchema(schemaCache.get(cacheKey)!);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await fetchData<FieldSchema>(
          `/field-schema/${domainKey}/${tableType}/`
        );
        schemaCache.set(cacheKey, data);
        setSchema(data);
      } catch (err: any) {
        logger.error(`[useFieldSchema] Error fetching schema:`, err);
        setError(err.message || "Failed to fetch field schema");
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, [domainKey, tableType, cacheKey]);

  const getFieldInfo = useCallback(
    (fieldName: string): FieldInfo | undefined => {
      return schema?.fields[fieldName];
    },
    [schema]
  );

  const getChoices = useCallback(
    (fieldName: string): FieldChoice[] => {
      return schema?.fields[fieldName]?.choices || [];
    },
    [schema]
  );

  return {
    schema,
    loading,
    error,
    getFieldInfo,
    getChoices,
  };
}

// Clear schema cache (useful after model changes)
export function invalidateSchemaCache(domainKey?: string, tableType?: string) {
  if (domainKey && tableType) {
    schemaCache.delete(`${domainKey}:${tableType}`);
  } else {
    schemaCache.clear();
  }
}

export default useFieldSchema;
